/**
 * Stale-chunk recovery guard.
 *
 * When a new frontend deploy replaces the hashed chunk files that an
 * already-open tab references, dynamic `import()`s for the old chunks fail
 * ("Failed to fetch dynamically imported module" / a CF Pages SPA fallback
 * served as text/html → "disallowed MIME type"). The fix is to reload once and
 * pick up the fresh index.html + chunks.
 *
 * The previous guard was a boolean `sessionStorage['chunk-load-reloaded']` that
 * a `window` `load` listener cleared on every page load. With 45 lazy routes,
 * chunk errors fire on *navigation* — after `load` has already cleared the flag
 * — so every reload looked like the first one and the page reload-looped
 * forever. This module replaces that with a timestamp guard that survives the
 * reload: reload at most once per cooldown window; a second failure inside the
 * window returns false so the caller stops and lets the ErrorBoundary UI show.
 * After the window a genuinely new deploy can recover again.
 *
 * Durability: the guard is useless if its timestamp can't survive the reload,
 * so we don't rely on sessionStorage alone. When storage is blocked (Safari
 * "block all site data", privacy extensions → `getItem`/`setItem` throw, or
 * quota-0 private mode where the write silently doesn't persist), we fall back
 * to `window.name`, which survives same-tab reloads without writable storage.
 * Without this, storage-blocked users would still tight-loop — the exact bug
 * this guard exists to prevent.
 *
 * ponytail: rolling 20s cooldown, not a hard latch — a source that *keeps*
 * re-firing a dead import reloads once per 20s rather than tight-looping. That's
 * acceptable for the "stale deploy fixed by one reload" case; add a bounded
 * attempt counter if a repeatedly-firing source ever proves to be a problem.
 */

const RELOAD_TS_KEY = 'chunk-reload-ts';
const RELOAD_COOLDOWN_MS = 20_000;

// window.name fallback marker (storage-blocked tabs). Namespaced + preserves any
// existing window.name content so we don't clobber other same-tab usage.
const WINDOW_NAME_RE = /\[chunk-reload-ts:(\d+)\]/;

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'disallowed MIME type', // Firefox: "blocked because of a disallowed MIME type"
  'responded with a MIME type', // Chrome: "...server responded with a MIME type of text/html"
];

/** True if the message looks like a stale-chunk / module-load failure. */
export function isChunkLoadError(message: string | null | undefined): boolean {
  return !!message && CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function readWindowNameTs(): number {
  const match = WINDOW_NAME_RE.exec(window.name || '');
  return match ? Number(match[1]) || 0 : 0;
}

function writeWindowNameTs(ts: number): void {
  const cleaned = (window.name || '').replace(WINDOW_NAME_RE, '');
  window.name = `[chunk-reload-ts:${ts}]${cleaned}`;
}

/** Last recovery-reload time (ms). Prefers sessionStorage, falls back to window.name. */
function readLastReload(): number {
  try {
    const stored = sessionStorage.getItem(RELOAD_TS_KEY);
    if (stored) {
      return Number(stored) || 0;
    }
  } catch {
    /* storage blocked → use window.name fallback below */
  }
  return readWindowNameTs();
}

/** Stamp the recovery-reload time. Always mirrors to window.name if storage write fails. */
function writeLastReload(ts: number): void {
  try {
    sessionStorage.setItem(RELOAD_TS_KEY, String(ts));
    return;
  } catch {
    /* storage blocked → fall through to window.name */
  }
  writeWindowNameTs(ts);
}

/**
 * Returns true (and stamps the time) if a recovery reload should happen now.
 * Returns false when a reload already happened within the cooldown window — the
 * caller must then stop reloading and surface the "please reload" UI instead of
 * looping.
 */
export function shouldReloadForChunkError(): boolean {
  const now = Date.now();
  if (now - readLastReload() < RELOAD_COOLDOWN_MS) {
    return false;
  }
  writeLastReload(now);
  return true;
}
